import { defineAbility, MongoAbility } from "@casl/ability";
import { USER_ROLES } from "@via/utils/constants.tsx";

// Actions represent different types of actions that can be performed on subjects.
// View: Used for show/hide functionality.
// Edit: Used for read-only functionality.
// Submit: Used for enable/disable functionality.
type Actions = "View" | "Edit" | "Submit";

// Subjects represent different types of subjects that can be acted upon.
// Input: Represents all input fields.
// Button: Represents all buttons.
type Subjects = "Input" | "Button" | "Drawer" | "MenuItem";

export type AppAbility = MongoAbility<[Actions, Subjects]>;

export type DynamicAbilities = {
  action: Actions;
  subject: Subjects;
  condition?: boolean | (() => boolean);
};

// Abilities based on user role
export function createAbilityForRole(
  role: string,
  dynamicAbilities: DynamicAbilities[] = []
): AppAbility {
  return defineAbility((can, cannot) => {
    switch (role as USER_ROLES) {
      case USER_ROLES.ADMIN:
        can("View", "Input"); // admin can view all inputs
        can("View", "Button"); // admin can view all buttons
        can("Edit", "Input"); // admin can edit all inputs
        can("Submit", "Button"); // admin can submit all buttons
        can("View", "Drawer"); // admin can view all drawers
        can("View", "MenuItem"); // admin can view all menu items
        break;
      case USER_ROLES.USER:
        can("View", "Input"); // user can view any inputs
        cannot("Edit", "Input"); // user cannot edit any inputs
        can("View", "Button"); // user can view any buttons
        cannot("Submit", "Button"); // user cannot submit any buttons
        can("View", "Drawer"); // user can view any drawers
        can("View", "MenuItem"); // user can view any menu items
        break;
      default:
        cannot("View", "Input"); // default cannot view any inputs
        cannot("View", "Button"); // default cannot view any buttons
        cannot("Edit", "Input"); // default cannot edit any inputs
        cannot("Submit", "Button"); // default cannot submit any buttons
        cannot("View", "Drawer"); // default cannot view any drawers
        cannot("View", "MenuItem"); // default cannot view any menu items
        break;
    }
    if (role !== USER_ROLES.ADMIN) {
      // Apply any extra abilities provided dynamically
      dynamicAbilities.forEach(({ action, subject, condition }) => {
        if (condition) {
          can(action, subject);
        } else {
          cannot(action, subject);
        }
      });
    }
  }) as AppAbility;
}
